import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Approvals } from "@/pages/admin/Approvals";
import { CategoriesManager } from "@/pages/admin/CategoriesManager";
import { LearnManager } from "@/pages/admin/LearnManager";
import { CornerManager } from "@/pages/admin/CornerManager";
import { ReviewsManager } from "@/pages/admin/ReviewsManager";
import { PayoutsManager } from "@/pages/admin/PayoutsManager";
import { NewsletterManager } from "@/pages/admin/NewsletterManager";
import { MessagesInbox } from "@/pages/admin/MessagesInbox";
import { UsersManager } from "@/pages/admin/UsersManager";
import { EmailList } from "@/pages/admin/EmailList";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  const refreshStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    refreshStats();
  }, [refreshStats]);

  const statTiles = stats ? [
    ["Pending approvals", stats.practitioners_pending + stats.learn_pending],
    ["Live practitioners", stats.practitioners_approved],
    ["Paid bookings", stats.bookings_paid],
    ["Commission (CAD)", `$${stats.commission_earned}`],
    ["Review holds", stats.reviews_on_hold],
    ["Members", stats.users],
    ["Email list", stats.emails],
    ["Learn entries", stats.learn_published],
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-2">The Team's Desk</h1>
      <p className="text-sm text-muted-foreground mb-10">Everything the community needs from you, in one calm place.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12" data-testid="admin-stats">
        {statTiles.map(([label, value]) => (
          <div key={label} className="silk-card rounded-lg p-4">
            <p className="font-display text-3xl text-primary">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="approvals">
        <TabsList className="mb-8 flex-wrap h-auto">
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">Approvals</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Review holds</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="learn" data-testid="admin-tab-learn">Learn Library</TabsTrigger>
          <TabsTrigger value="corner" data-testid="admin-tab-corner">Bridgett's Corner</TabsTrigger>
          <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">
            Messages{stats?.messages_waiting > 0 && <span className="ml-1.5 rounded-full bg-primary/15 text-primary text-[10px] px-1.5 py-0.5" data-testid="admin-tab-messages-waiting">{stats.messages_waiting}</span>}
          </TabsTrigger>
          <TabsTrigger value="emails" data-testid="admin-tab-emails">Email list</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals"><Approvals onChange={refreshStats} /></TabsContent>
        <TabsContent value="reviews"><ReviewsManager onChange={refreshStats} /></TabsContent>
        <TabsContent value="payouts"><PayoutsManager /></TabsContent>
        <TabsContent value="categories"><CategoriesManager /></TabsContent>
        <TabsContent value="learn"><LearnManager onChange={refreshStats} /></TabsContent>
        <TabsContent value="corner"><CornerManager onChange={refreshStats} /></TabsContent>
        <TabsContent value="newsletter"><NewsletterManager /></TabsContent>
        <TabsContent value="messages"><MessagesInbox onChange={refreshStats} /></TabsContent>
        <TabsContent value="users"><UsersManager onChange={refreshStats} /></TabsContent>
        <TabsContent value="emails"><EmailList onChange={refreshStats} /></TabsContent>
      </Tabs>
    </div>
  );
}
